import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { convertToCurrencyCodeFormat, formatDate } from "@monorepo/helpers";
import { useAddClientCouponMutation, useRemoveClientCouponMutation } from "@monorepo/services/api/order";
import theme from "@monorepo/styles/theme";
import { ICoupon } from "@monorepo/types";
import LoadingButton from "@mui/lab/LoadingButton";
import { Card, CardActions, CardContent, Typography } from "@mui/material";

import { ButtonStyle, CardStyle } from "./CouponCard.styles";

interface IProps {
  coupon: ICoupon;
  onCouponUsed: () => void;
  onCouponRemove: () => void;
  shouldResetError: boolean;
}

export const CouponCard: React.FC<IProps> = ({ coupon, onCouponUsed, onCouponRemove, shouldResetError }: IProps) => {
  const { t } = useTranslation();
  const [addClientCoupon, { isLoading: isAdding }] = useAddClientCouponMutation();
  const [removeUniqueCoupon, { isLoading: isRemoving }] = useRemoveClientCouponMutation();

  useEffect(() => {
    if (shouldResetError) {
      setError("");
    }
  }, [shouldResetError]);

  const onUseCoupon = () => {
    addClientCoupon(coupon.id)
      .unwrap()
      .then(() => {
        toast.success(t("coupons.added_successfully"));
        onCouponUsed();
      })
      .catch((error) => setError(error.message));
  };

  const onDontUseCoupon = () => {
    removeUniqueCoupon(coupon.id)
      .unwrap()
      .then(() => {
        toast.success(t("coupons.removed_successfully"));
        onCouponRemove();
      })
      .catch((error) => setError(error.message));
  };

  const [error, setError] = useState<string>();

  return (
    <>
      {error && <Typography color="error">{error}</Typography>}
      <Card sx={CardStyle}>
        <CardContent>
          <Typography
            variant="h5"
            fontWeight="bold"
            sx={{
              color: theme.palette.text.secondary,
              align: "left",
            }}
          >
            {convertToCurrencyCodeFormat(coupon.amount)}
          </Typography>
          {coupon.valid_until && (
            <Typography
              variant="body2"
              sx={{
                marginTop: 3,
              }}
            >{`${t("coupons.valid_until")} ${formatDate(coupon.valid_until)}`}</Typography>
          )}
        </CardContent>
        <CardActions sx={{ padding: 0 }}>
          <LoadingButton
            fullWidth
            sx={ButtonStyle(!!coupon.order_id)}
            onClick={coupon.order_id ? onDontUseCoupon : onUseCoupon}
            variant="contained"
            color={coupon.order_id ? "secondary" : "primary"}
            loading={isAdding || isRemoving}
          >
            {coupon.order_id ? t("coupons.dont_use_coupon") : t("coupons.use_coupon")}
          </LoadingButton>
        </CardActions>
      </Card>
    </>
  );
};
